import React, {Fragment} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, gap, hiddenScroll, mr} from "@pg-design/helpers-css-strings";

import {HEADER_CELL_WIDTH, HEADER_CELL_WIDTH_MOBILE} from "../constants/table_sizes";
import {IUseComparisonTableReturnType} from "../hooks/use_comparison_table";
import {useShowScrollButtons} from "../hooks/use_show_scroll_buttons";
import {useStickyHeaderMaxWidth} from "../hooks/use_sticky_header_max_width";
import {useTableHeaderSticky} from "../hooks/use_table_header_sticky";
import {cellWidth, cellWidthMd, cellWidthPrint, cellWidthSm, cellWidthXl} from "../styles/table_cell_styles";
import {IComparisonTableCellHeader} from "../types/IComparisonTableCellHeader";
import {IComparisonTableElement} from "../types/IComparisonTableElement";
import {Cell} from "./cell/Cell";
import {CellHeader} from "./cell/CellHeader";
import {Row} from "./row/Row";
import {RowHeader} from "./row/RowHeader";
import {TableScrollButton} from "./TableScrollButton";

type ITableProps<TTableItemData extends IComparisonTableElement> = IUseComparisonTableReturnType<TTableItemData>["tableProps"];
interface IComparisonTableScrollButtonConfig {
    topMobile?: string;
    topDesktop?: string;
    color?: string;
    leftBtnMobile?: {right?: string; left?: string};
    rightBtnMobile?: {right?: string; left?: string};
}

export interface IComparisonTableProps<TTableItemData extends IComparisonTableElement> extends ITableProps<TTableItemData> {
    cellHeader: IComparisonTableCellHeader<TTableItemData>;
    data: TTableItemData[];
    disabledItemIds?: IComparisonTableElement["id"][];
    headerStyle?: Interpolation<Theme>;
    headerClassName?: string;
    gap?: Parameters<typeof gap>[0];
    shrinkWidthToData?: boolean;
    hideNavigationOnMobile?: boolean;
    scrollButtonsConfig?: IComparisonTableScrollButtonConfig;
    className?: string;
    dataTestId?: string;
    placeholderColumn?: React.ReactNode;
}

export const ComparisonTable = <TTableItemData extends IComparisonTableElement>(props: IComparisonTableProps<TTableItemData>) => {
    const {
        data,
        refs,
        rows,
        tableSettings,
        tableNavigation,
        placeholderColumn,
        hideNavigationOnMobile,
        scrollButtonsConfig,
        dataTestId,
        cellHeader,
        disabledItemIds,
        gap,
        shrinkWidthToData,
        className,
        headerStyle,
        headerClassName
    } = props;
    const {topMobile = "7.4rem", topDesktop = "7.4rem", color: scrollButtonsColor, leftBtnMobile, rightBtnMobile} = scrollButtonsConfig ?? {};

    const showScrollButtons = useShowScrollButtons({
        tableRef: refs.tableRef,
        tableRowsRef: refs.tableRowsRef,
        data
    });

    const {stickyElementRef, elementBeforeStickyRef} = useTableHeaderSticky();
    useStickyHeaderMaxWidth({
        dataLength: data.length,
        stickyElementRef,
        tableRowsRef: refs.tableRowsRef,
        shrinkWidthToData
    });

    return (
        <div css={wrapper} className={className} data-testid={dataTestId}>
            <div ref={elementBeforeStickyRef} css={elementBeforeStickyStyle} />
            <div ref={stickyElementRef} css={[headerWrap, headerStyle]} className={headerClassName}>
                <div css={headerFirstCell}>
                    {/*
                        That's a place for the big add button in upcoming sprints
                    */}
                </div>
                <HeaderContent gapValue={gap} ref={refs.headerRef}>
                    {data.map((item, index: number) => {
                        const CellHeaderComponent = cellHeader;
                        const isDisabled = disabledItemIds?.includes(item.id);
                        return (
                            <CellHeader key={item.id} isDisabled={isDisabled}>
                                <CellHeaderComponent item={item} index={index} />
                            </CellHeader>
                        );
                    })}
                    {placeholderColumn && <CellHeader>{placeholderColumn}</CellHeader>}
                </HeaderContent>
                {showScrollButtons && (
                    <Fragment>
                        <div css={[scrollButtonPositioner(true, topMobile, topDesktop, leftBtnMobile), hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton left onClick={tableNavigation.scrollLeft} color={scrollButtonsColor} />
                        </div>
                        <div css={[scrollButtonPositioner(false, topMobile, topDesktop, rightBtnMobile), hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton onClick={tableNavigation.scrollRight} color={scrollButtonsColor} />
                        </div>
                    </Fragment>
                )}
            </div>
            <div ref={refs.tableRef} css={table}>
                <div ref={refs.tableRowsRef} css={[tableRows, shrinkWidthToData && shrinkRowsWidthToDataStyle]}>
                    {rows.map((row) => (
                        <Row key={row.key}>
                            {(height) => (
                                <Fragment>
                                    <RowHeader headerIconsSettings={tableSettings.headerIcons} height={height} headerDefinition={row.rowHeader} />

                                    {data.map((item, index) => {
                                        const value = row.key in item ? item[row.key as keyof TTableItemData] : null;
                                        const cellCss = gap && (index < data.length - 1 || !!placeholderColumn) ? createTableCellCss(gap) : undefined;
                                        const isDisabled = disabledItemIds?.includes(item.id);

                                        return (
                                            <Cell
                                                isDisabled={isDisabled}
                                                height={height}
                                                key={item.id}
                                                item={item}
                                                value={value}
                                                cellComponent={row.cell}
                                                css={cellCss}
                                            />
                                        );
                                    })}
                                    {placeholderColumn && (
                                        <div css={appendColumnRowCellStyle}>
                                            <div css={appendColumnSkeletonBarStyle} />
                                        </div>
                                    )}
                                </Fragment>
                            )}
                        </Row>
                    ))}
                </div>
            </div>
        </div>
    );
};

const wrapper = css`
    position: relative;

    @media print {
        width: 100%;
        height: 100%;
    }
`;

const elementBeforeStickyStyle = css`
    height: 1px;
    width: 100%;
    visibility: hidden;
`;

const headerWrap = css`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
`;

const headerFirstCell = (theme: Theme) => css`
    flex: 0 0 ${HEADER_CELL_WIDTH_MOBILE};

    @media (min-width: ${theme.breakpoints.md}) {
        flex: 0 0 ${HEADER_CELL_WIDTH};
    }
`;

const HeaderContent = styled.div<{gapValue?: Parameters<typeof gap>[0]}>`
    overflow: auto;
    display: flex;
    ${hiddenScroll()}
    ${({gapValue}) => gap(gapValue)};
`;

const shrinkRowsWidthToDataStyle = css`
    width: fit-content;
`;

const table = css`
    overflow: auto;
    overflow-scrolling: unset;
    ${hiddenScroll()}
`;

const tableRows = (theme: Theme) => css`
    display: grid;
    margin-left: ${HEADER_CELL_WIDTH_MOBILE}; // must be equal to the "width" value of the first (header) column
    column-gap: ${calculateRemSize(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        margin-left: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        &:before {
            width: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        }
    }

    /*
      Paint rows
     */
    & > div:nth-of-type(even) {
        background-color: #f2f2f2;

        & > div:first-of-type > span {
            background-color: #f2f2f2;
        }
    }

    & > div:nth-of-type(odd) {
        background-color: #fff;

        & > div:first-of-type > span {
            background-color: #fff;
        }
    }
`;

const createTableCellCss = (gapValue: Parameters<typeof gap>[0]) => css`
    ${mr(gapValue)};
`;

const scrollButtonPositioner =
    (isLeft: boolean, topMobile = "7.4rem", topDesktop = "7.4rem", mobilePos?: {right?: string; left?: string}) =>
    (theme: Theme) => css`
        position: absolute;
        z-index: 10;
        top: ${topMobile};
        right: ${mobilePos ? (mobilePos.right ?? "unset") : isLeft ? "unset" : "0"};
        left: ${mobilePos ? (mobilePos.left ?? "unset") : isLeft ? HEADER_CELL_WIDTH_MOBILE : "unset"};
        transform: ${mobilePos ? "none" : `translateX(${isLeft ? "-50%" : "50%"})`};

        @media (min-width: ${theme.breakpoints.md}) {
            top: ${topDesktop};
            right: ${isLeft ? "unset" : "0"};
            left: ${isLeft ? HEADER_CELL_WIDTH : "unset"};
            transform: translateX(${isLeft ? "-50%" : "50%"});
        }
    `;

const hideOnMobileStyle = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const appendColumnRowCellStyle = (theme: Theme) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${cellWidthSm};

    @media (min-width: ${theme.breakpoints.sm}) {
        ${cellWidthMd};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${cellWidthXl};
    }

    @media (min-width: ${theme.breakpoints.xl}) {
        ${cellWidth};
    }

    @media print {
        ${cellWidthPrint};
    }
`;

const appendColumnSkeletonBarStyle = css`
    height: 12px;
    width: 80px;
    background-color: #d6d6d6;
    border-radius: 2px;
`;
import React, {Fragment} from "react";
import {css, Interpolation, Theme} from "@emotion/react";
import styled from "@emotion/styled";

import {calculateRemSize, gap, hiddenScroll, mr} from "@pg-design/helpers-css-strings";

import {HEADER_CELL_WIDTH, HEADER_CELL_WIDTH_MOBILE} from "../constants/table_sizes";
import {IUseComparisonTableReturnType} from "../hooks/use_comparison_table";
import {useShowScrollButtons} from "../hooks/use_show_scroll_buttons";
import {useStickyHeaderMaxWidth} from "../hooks/use_sticky_header_max_width";
import {useTableHeaderSticky} from "../hooks/use_table_header_sticky";
import {cellWidth, cellWidthMd, cellWidthPrint, cellWidthSm, cellWidthXl} from "../styles/table_cell_styles";
import {IComparisonTableCellHeader} from "../types/IComparisonTableCellHeader";
import {IComparisonTableElement} from "../types/IComparisonTableElement";
import {Cell} from "./cell/Cell";
import {CellHeader} from "./cell/CellHeader";
import {Row} from "./row/Row";
import {RowHeader} from "./row/RowHeader";
import {TableScrollButton} from "./TableScrollButton";

type ITableProps<TTableItemData extends IComparisonTableElement> = IUseComparisonTableReturnType<TTableItemData>["tableProps"];
interface IComparisonTableScrollButtonConfig {
    topMobile?: string;
    topDesktop?: string;
    color?: string;
    leftBtnMobile?: {right?: string; left?: string};
    rightBtnMobile?: {right?: string; left?: string};
}

export interface IComparisonTableProps<TTableItemData extends IComparisonTableElement> extends ITableProps<TTableItemData> {
    cellHeader: IComparisonTableCellHeader<TTableItemData>;
    data: TTableItemData[];
    disabledItemIds?: IComparisonTableElement["id"][];
    headerStyle?: Interpolation<Theme>;
    headerClassName?: string;
    gap?: Parameters<typeof gap>[0];
    shrinkWidthToData?: boolean;
    hideNavigationOnMobile?: boolean;
    scrollButtonsConfig?: IComparisonTableScrollButtonConfig;
    className?: string;
    dataTestId?: string;
    placeholderColumn?: React.ReactNode;
}

export const ComparisonTable = <TTableItemData extends IComparisonTableElement>(props: IComparisonTableProps<TTableItemData>) => {
    const {
        data,
        refs,
        rows,
        tableSettings,
        tableNavigation,
        placeholderColumn,
        hideNavigationOnMobile,
        scrollButtonsConfig,
        dataTestId,
        cellHeader,
        disabledItemIds,
        gap,
        shrinkWidthToData,
        className,
        headerStyle,
        headerClassName
    } = props;
    const {topMobile = "7.4rem", topDesktop = "7.4rem", color: scrollButtonsColor, leftBtnMobile, rightBtnMobile} = scrollButtonsConfig ?? {};

    const showScrollButtons = useShowScrollButtons({
        tableRef: refs.tableRef,
        tableRowsRef: refs.tableRowsRef,
        data
    });

    const {stickyElementRef, elementBeforeStickyRef} = useTableHeaderSticky();
    useStickyHeaderMaxWidth({
        dataLength: data.length,
        stickyElementRef,
        tableRowsRef: refs.tableRowsRef,
        shrinkWidthToData
    });

    return (
        <div css={wrapper} className={className} data-testid={dataTestId}>
            <div ref={elementBeforeStickyRef} css={elementBeforeStickyStyle} />
            <div ref={stickyElementRef} css={[headerWrap, headerStyle]} className={headerClassName}>
                <div css={headerFirstCell}>
                    {/*
                        That's a place for the big add button in upcoming sprints
                    */}
                </div>
                <HeaderContent gapValue={gap} ref={refs.headerRef}>
                    {data.map((item, index: number) => {
                        const CellHeaderComponent = cellHeader;
                        const isDisabled = disabledItemIds?.includes(item.id);
                        return (
                            <CellHeader key={item.id} isDisabled={isDisabled}>
                                <CellHeaderComponent item={item} index={index} />
                            </CellHeader>
                        );
                    })}
                    {placeholderColumn && <CellHeader>{placeholderColumn}</CellHeader>}
                </HeaderContent>
                {showScrollButtons && (
                    <Fragment>
                        <div css={[scrollButtonPositioner(true, topMobile, topDesktop, leftBtnMobile), hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton left onClick={tableNavigation.scrollLeft} color={scrollButtonsColor} />
                        </div>
                        <div css={[scrollButtonPositioner(false, topMobile, topDesktop, rightBtnMobile), hideNavigationOnMobile && hideOnMobileStyle]}>
                            <TableScrollButton onClick={tableNavigation.scrollRight} color={scrollButtonsColor} />
                        </div>
                    </Fragment>
                )}
            </div>
            <div ref={refs.tableRef} css={table}>
                <div ref={refs.tableRowsRef} css={[tableRows, shrinkWidthToData && shrinkRowsWidthToDataStyle]}>
                    {rows.map((row) => (
                        <Row key={row.key}>
                            {(height) => (
                                <Fragment>
                                    <RowHeader headerIconsSettings={tableSettings.headerIcons} height={height} headerDefinition={row.rowHeader} />

                                    {data.map((item, index) => {
                                        const value = row.key in item ? item[row.key as keyof TTableItemData] : null;
                                        const cellCss = gap && (index < data.length - 1 || !!placeholderColumn) ? createTableCellCss(gap) : undefined;
                                        const isDisabled = disabledItemIds?.includes(item.id);

                                        return (
                                            <Cell
                                                isDisabled={isDisabled}
                                                height={height}
                                                key={item.id}
                                                item={item}
                                                value={value}
                                                cellComponent={row.cell}
                                                css={cellCss}
                                            />
                                        );
                                    })}
                                    {placeholderColumn && (
                                        <div css={appendColumnRowCellStyle}>
                                            <div css={appendColumnSkeletonBarStyle} />
                                        </div>
                                    )}
                                </Fragment>
                            )}
                        </Row>
                    ))}
                </div>
            </div>
        </div>
    );
};

const wrapper = css`
    position: relative;

    @media print {
        width: 100%;
        height: 100%;
    }
`;

const elementBeforeStickyStyle = css`
    height: 1px;
    width: 100%;
    visibility: hidden;
`;

const headerWrap = css`
    position: sticky;
    top: 0;
    z-index: 10;
    display: flex;
`;

const headerFirstCell = (theme: Theme) => css`
    flex: 0 0 ${HEADER_CELL_WIDTH_MOBILE};

    @media (min-width: ${theme.breakpoints.md}) {
        flex: 0 0 ${HEADER_CELL_WIDTH};
    }
`;

const HeaderContent = styled.div<{gapValue?: Parameters<typeof gap>[0]}>`
    overflow: auto;
    display: flex;
    ${hiddenScroll()}
    ${({gapValue}) => gap(gapValue)};
`;

const shrinkRowsWidthToDataStyle = css`
    width: fit-content;
`;

const table = css`
    overflow: auto;
    overflow-scrolling: unset;
    ${hiddenScroll()}
`;

const tableRows = (theme: Theme) => css`
    display: grid;
    margin-left: ${HEADER_CELL_WIDTH_MOBILE}; // must be equal to the "width" value of the first (header) column
    column-gap: ${calculateRemSize(2)};

    @media (min-width: ${theme.breakpoints.md}) {
        margin-left: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        &:before {
            width: ${HEADER_CELL_WIDTH}; // must be equal to the "width" value of the first (header) column
        }
    }

    /*
      Paint rows
     */
    & > div:nth-of-type(even) {
        background-color: #f2f2f2;

        & > div:first-of-type > span {
            background-color: #f2f2f2;
        }
    }

    & > div:nth-of-type(odd) {
        background-color: #fff;

        & > div:first-of-type > span {
            background-color: #fff;
        }
    }
`;

const createTableCellCss = (gapValue: Parameters<typeof gap>[0]) => css`
    ${mr(gapValue)};
`;

const scrollButtonPositioner =
    (isLeft: boolean, topMobile = "7.4rem", topDesktop = "7.4rem", mobilePos?: {right?: string; left?: string}) =>
    (theme: Theme) => css`
        position: absolute;
        z-index: 10;
        top: ${topMobile};
        right: ${mobilePos ? (mobilePos.right ?? "unset") : isLeft ? "unset" : "0"};
        left: ${mobilePos ? (mobilePos.left ?? "unset") : isLeft ? HEADER_CELL_WIDTH_MOBILE : "unset"};
        transform: ${mobilePos ? "none" : `translateX(${isLeft ? "-50%" : "50%"})`};

        @media (min-width: ${theme.breakpoints.md}) {
            top: ${topDesktop};
            right: ${isLeft ? "unset" : "0"};
            left: ${isLeft ? HEADER_CELL_WIDTH : "unset"};
            transform: translateX(${isLeft ? "-50%" : "50%"});
        }
    `;

const hideOnMobileStyle = (theme: Theme) => css`
    @media (max-width: ${theme.breakpoints.md}) {
        display: none;
    }
`;

const appendColumnRowCellStyle = (theme: Theme) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    ${cellWidthSm};

    @media (min-width: ${theme.breakpoints.sm}) {
        ${cellWidthMd};
    }

    @media (min-width: ${theme.breakpoints.md}) {
        ${cellWidthXl};
    }

    @media (min-width: ${theme.breakpoints.xl}) {
        ${cellWidth};
    }

    @media print {
        ${cellWidthPrint};
    }
`;

const appendColumnSkeletonBarStyle = css`
    height: 12px;
    width: 80px;
    background-color: #d6d6d6;
    border-radius: 2px;
`;
